// pages/admin/AdminAttendance.jsx

import {
    AlertCircle,
    Award,
    Calendar,
    CheckCircle,
    Clock,
    RefreshCw,
    Search,
    User,
    XCircle
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loading } from '../../components/common/Loading';
import Navbar from '../../components/header/Navbar';
import { useAuth } from '../../hooks/useAuth';
import {
    approveOvertime,
    autoCheckOutAll,
    getAttendanceByDateAdmin,
    getCheckedInUsers,
    getOvertimeRequests,
    getTodayString,
    rejectOvertime
} from '../../services/attendanceService';

const AdminAttendance = () => {
    const { user, loading: isLoading, users } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [overtimeRequests, setOvertimeRequests] = useState([]);
    const [checkedInUsers, setCheckedInUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(getTodayString());
    const [viewMode, setViewMode] = useState('today');
    const [searchTerm, setSearchTerm] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [attendanceData, overtimeData, checkedInData] = await Promise.all([
                getAttendanceByDateAdmin(selectedDate),
                getOvertimeRequests(),
                getCheckedInUsers()
            ]);

            setAttendance(attendanceData);
            setOvertimeRequests(overtimeData);
            setCheckedInUsers(checkedInData);
        } catch (error) {
            console.error('Error loading attendance data:', error);
            toast.error('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [selectedDate]);

    // Handle overtime approval
    const handleApproveOvertime = async (userId, date) => {
        setActionLoading(true);
        try {
            await approveOvertime(userId, date);
            toast.success('Overtime approved successfully!');
            await loadData();
        } catch (error) {
            toast.error('Failed to approve overtime');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle overtime rejection
    const handleRejectOvertime = async (userId, date) => {
        if (!window.confirm('Are you sure you want to reject this overtime request?')) return;

        setActionLoading(true);
        try {
            await rejectOvertime(userId, date);
            toast.success('Overtime rejected');
            await loadData();
        } catch (error) {
            toast.error('Failed to reject overtime');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle manual auto check-out
    const handleAutoCheckOut = async () => {
        if (!window.confirm('This will check out all employees who are still checked in. Continue?')) return;

        setActionLoading(true);
        try {
            const result = await autoCheckOutAll();
            const successCount = result.filter(r => r.success).length;
            toast.success(`${successCount} employees checked out`);
            await loadData();
        } catch (error) {
            toast.error('Failed to auto check-out');
        } finally {
            setActionLoading(false);
        }
    };

    // Format duration
    const formatDuration = (minutes) => {
        if (!minutes) return '0h 0m';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    // Filter attendance by search
    const filteredAttendance = attendance.filter(record => {
        const userName = record.userName?.toLowerCase() || '';
        const userId = record.userId?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return userName.includes(search) || userId.includes(search);
    });

    // Calculate stats
    const stats = {
        total: attendance.length,
        checkedIn: attendance.filter(a => a.status === 'checked-in' || a.status === 'on-break').length,
        onBreak: attendance.filter(a => a.status === 'on-break').length,
        checkedOut: attendance.filter(a => a.status === 'checked-out').length,
        notCheckedIn: attendance.filter(a => !a.checkIn && !a.sessions?.length).length,
        overtimePending: overtimeRequests.length
    };

    // Get status badge color
    const getStatusBadge = (status) => {
        const configs = {
            'not-checked-in': { color: 'bg-gray-100 text-gray-600', label: 'Not Checked In' },
            'checked-in': { color: 'bg-green-100 text-green-700', label: 'Checked In' },
            'on-break': { color: 'bg-yellow-100 text-yellow-700', label: 'On Break' },
            'checked-out': { color: 'bg-blue-100 text-blue-700', label: 'Checked Out' }
        };
        return configs[status] || configs['not-checked-in'];
    };

    // Render today's attendance table
    const renderTodayView = () => (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Today's Attendance</h3>
                        <p className="text-sm text-gray-500">{selectedDate}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                    <button
                        onClick={loadData}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-1 text-sm"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                    <button
                        onClick={handleAutoCheckOut}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition flex items-center gap-1 text-sm disabled:opacity-50"
                    >
                        <Clock className="w-4 h-4" />
                        Auto Check-out All
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gray-50/50">
                <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500">Total Employees</p>
                    <p className="text-xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500">Checked In</p>
                    <p className="text-xl font-bold text-green-600">{stats.checkedIn}</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500">On Break</p>
                    <p className="text-xl font-bold text-yellow-600">{stats.onBreak}</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500">Checked Out</p>
                    <p className="text-xl font-bold text-blue-600">{stats.checkedOut}</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500">Not Checked In</p>
                    <p className="text-xl font-bold text-gray-400">{stats.notCheckedIn}</p>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <div className="p-4">
                    <div className="relative max-w-xs">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sessions</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Break</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Overtime</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredAttendance.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-4xl">📋</span>
                                        <p className="font-medium">No attendance records</p>
                                        <p className="text-sm text-gray-400">No records found for this date</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredAttendance.map((record) => {
                                const status = getStatusBadge(record.status);
                                const sessionCount = record.sessions?.length || 0;
                                const workMinutes = record.totalWorkDuration || 0;
                                const breakMinutes = record.totalBreakTime || 0;

                                // Get latest session check-in/out times
                                const lastSession = record.sessions?.[record.sessions.length - 1];
                                const checkInTime = lastSession?.checkIn?.time?.seconds
                                    ? new Date(lastSession.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : '-';
                                const checkOutTime = lastSession?.checkOut?.time?.seconds
                                    ? new Date(lastSession.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : '-';

                                return (
                                    <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 flex items-center justify-center text-white font-semibold text-xs">
                                                    {record.userName?.charAt(0) || 'U'}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-800">{record.userName || 'Unknown'}</p>
                                                    <p className="text-xs text-gray-400">{record.userId?.substring(0, 8)}...</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                                                {status.label}
                                            </span>
                                            {record.sessions?.some(s => s.isOvertime && !s.checkOut) && (
                                                <span className="ml-1 text-xs text-orange-500">⏰</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="text-gray-600">{sessionCount} session{sessionCount > 1 ? 's' : ''}</p>
                                                {sessionCount > 0 && (
                                                    <p className="text-xs text-gray-400">
                                                        Last: {checkInTime} - {checkOutTime}
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {workMinutes > 0 ? formatDuration(workMinutes) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {breakMinutes > 0 ? `${breakMinutes}m` : '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            {record.overtime?.isOvertime ? (
                                                <span className={`text-xs font-medium ${record.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
                                                    {record.overtime.minutes}m
                                                    {!record.overtime.approved && ' (Pending)'}
                                                </span>
                                            ) : record.overtime?.requested ? (
                                                <span className="text-xs text-yellow-600 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" />
                                                    Requested
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Render overtime requests view
    const renderOvertimeView = () => (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                        <Award className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Overtime Requests</h3>
                        <p className="text-sm text-gray-500">{overtimeRequests.length} pending approval</p>
                    </div>
                </div>
            </div>

            {overtimeRequests.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">✅</div>
                    <p className="font-medium">No pending overtime requests</p>
                    <p className="text-sm text-gray-400">All overtime has been approved</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sessions</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Work</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Overtime</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {overtimeRequests.map((record) => {
                                const sessionCount = record.sessions?.length || 0;
                                const totalWork = record.totalWorkDuration || 0;
                                const isManualRequest = record.overtime?.requested && !record.overtime?.isOvertime;
                                const date = record.date ? new Date(record.date).toLocaleDateString() : '-';

                                return (
                                    <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-400 to-red-400 flex items-center justify-center text-white font-semibold text-xs">
                                                    {record.userName?.charAt(0) || 'U'}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-800">{record.userName || 'Unknown'}</p>
                                                    <p className="text-xs text-gray-400">{record.userId?.substring(0, 8)}...</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{date}</td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {sessionCount} session{sessionCount > 1 ? 's' : ''}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {formatDuration(totalWork)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="font-medium text-orange-600">
                                                {record.overtime?.minutes || 0}m
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {isManualRequest ? (
                                                <span className="inline-flex items-center gap-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
                                                    <User className="w-3 h-3" />
                                                    Manual
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                                    <Clock className="w-3 h-3" />
                                                    Auto
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleApproveOvertime(record.userId, record.date)}
                                                    disabled={actionLoading}
                                                    className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-xs flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    <CheckCircle className="w-3 h-3" />
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleRejectOvertime(record.userId, record.date)}
                                                    disabled={actionLoading}
                                                    className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-xs flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    <XCircle className="w-3 h-3" />
                                                    Reject
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    if (loading || isLoading) return <Loading />;

    return (
        <>
            <Navbar user={user} />
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Attendance Management</h1>
                        <p className="text-sm text-gray-500">Monitor and manage employee attendance</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setViewMode('today')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${viewMode === 'today'
                                ? 'bg-pink-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Today
                        </button>
                        <button
                            onClick={() => setViewMode('overtime')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1 ${viewMode === 'overtime'
                                ? 'bg-pink-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <Award className="w-4 h-4" />
                            Overtime
                            {overtimeRequests.length > 0 && (
                                <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                    {overtimeRequests.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Content */}
                {viewMode === 'today' ? renderTodayView() : renderOvertimeView()}
            </div>
        </>
    );
};

export default AdminAttendance;

// // pages/admin/AdminAttendance.jsx

// import {
//     Award,
//     Calendar,
//     CheckCircle,
//     Clock,
//     RefreshCw,
//     Search
// } from 'lucide-react';
// import { useEffect, useState } from 'react';
// import toast from 'react-hot-toast';
// import { Loading } from '../../components/common/Loading';
// import Navbar from '../../components/header/Navbar';
// import { useAuth } from '../../hooks/useAuth';
// import {
//     approveOvertime,
//     autoCheckOutAll,
//     getAttendanceByDateAdmin,
//     getCheckedInUsers,
//     getOvertimeRequests,
//     getTodayString
// } from '../../services/attendanceService';

// const AdminAttendance = () => {
//     const { user, loading: isLoading, users } = useAuth();
//     const [attendance, setAttendance] = useState([]);
//     const [overtimeRequests, setOvertimeRequests] = useState([]);
//     const [checkedInUsers, setCheckedInUsers] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [selectedDate, setSelectedDate] = useState(getTodayString());
//     const [viewMode, setViewMode] = useState('today'); // 'today', 'overtime', 'history'
//     const [searchTerm, setSearchTerm] = useState('');
//     const [actionLoading, setActionLoading] = useState(false);

//     const loadData = async () => {
//         setLoading(true);
//         try {
//             const [attendanceData, overtimeData, checkedInData] = await Promise.all([
//                 getAttendanceByDateAdmin(selectedDate),
//                 getOvertimeRequests(),
//                 getCheckedInUsers()
//             ]);

//             setAttendance(attendanceData);
//             setOvertimeRequests(overtimeData);
//             setCheckedInUsers(checkedInData);
//         } catch (error) {
//             console.error('Error loading attendance data:', error);
//             toast.error('Failed to load attendance data');
//         } finally {
//             setLoading(false);
//         }
//     };

//     useEffect(() => {
//         loadData();
//     }, [selectedDate]);

//     // Handle overtime approval
//     const handleApproveOvertime = async (userId, date) => {
//         setActionLoading(true);
//         try {
//             await approveOvertime(userId, date);
//             toast.success('Overtime approved successfully!');
//             await loadData();
//         } catch (error) {
//             toast.error('Failed to approve overtime');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Handle manual auto check-out
//     const handleAutoCheckOut = async () => {
//         if (!window.confirm('This will check out all employees who are still checked in. Continue?')) return;

//         setActionLoading(true);
//         try {
//             const result = await autoCheckOutAll();
//             toast.success(`${result.filter(r => r.success).length} employees checked out`);
//             await loadData();
//         } catch (error) {
//             toast.error('Failed to auto check-out');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     // Filter attendance by search
//     const filteredAttendance = attendance.filter(record => {
//         const userName = record.userName?.toLowerCase() || '';
//         const userId = record.userId?.toLowerCase() || '';
//         const search = searchTerm.toLowerCase();
//         return userName.includes(search) || userId.includes(search);
//     });

//     // Calculate stats
//     const stats = {
//         total: attendance.length,
//         checkedIn: attendance.filter(a => a.status === 'checked-in' || a.status === 'on-break').length,
//         onBreak: attendance.filter(a => a.status === 'on-break').length,
//         checkedOut: attendance.filter(a => a.status === 'checked-out').length,
//         notCheckedIn: attendance.filter(a => !a.checkIn).length,
//         overtimePending: overtimeRequests.length
//     };

//     // Get status badge color
//     const getStatusBadge = (status) => {
//         const configs = {
//             'not-checked-in': { color: 'bg-gray-100 text-gray-600', label: 'Not Checked In' },
//             'checked-in': { color: 'bg-green-100 text-green-700', label: 'Checked In' },
//             'on-break': { color: 'bg-yellow-100 text-yellow-700', label: 'On Break' },
//             'checked-out': { color: 'bg-blue-100 text-blue-700', label: 'Checked Out' }
//         };
//         return configs[status] || configs['not-checked-in'];
//     };

//     // Render today's attendance table
//     const renderTodayView = () => (
//         <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
//             <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gradient-to-r from-gray-50 to-white">
//                 <div className="flex items-center gap-3">
//                     <div className="p-2 bg-blue-100 rounded-lg">
//                         <Clock className="w-5 h-5 text-blue-600" />
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Today's Attendance</h3>
//                         <p className="text-sm text-gray-500">{selectedDate}</p>
//                     </div>
//                 </div>
//                 <div className="flex items-center gap-3">
//                     <input
//                         type="date"
//                         value={selectedDate}
//                         onChange={(e) => setSelectedDate(e.target.value)}
//                         className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
//                     />
//                     <button
//                         onClick={loadData}
//                         className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-1 text-sm"
//                     >
//                         <RefreshCw className="w-4 h-4" />
//                         Refresh
//                     </button>
//                     <button
//                         onClick={handleAutoCheckOut}
//                         disabled={actionLoading}
//                         className="px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition flex items-center gap-1 text-sm disabled:opacity-50"
//                     >
//                         <Clock className="w-4 h-4" />
//                         Auto Check-out All
//                     </button>
//                 </div>
//             </div>

//             {/* Stats Cards */}
//             <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gray-50/50">
//                 <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
//                     <p className="text-xs text-gray-500">Total Employees</p>
//                     <p className="text-xl font-bold text-gray-800">{stats.total}</p>
//                 </div>
//                 <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
//                     <p className="text-xs text-gray-500">Checked In</p>
//                     <p className="text-xl font-bold text-green-600">{stats.checkedIn}</p>
//                 </div>
//                 <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
//                     <p className="text-xs text-gray-500">On Break</p>
//                     <p className="text-xl font-bold text-yellow-600">{stats.onBreak}</p>
//                 </div>
//                 <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
//                     <p className="text-xs text-gray-500">Checked Out</p>
//                     <p className="text-xl font-bold text-blue-600">{stats.checkedOut}</p>
//                 </div>
//                 <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
//                     <p className="text-xs text-gray-500">Not Checked In</p>
//                     <p className="text-xl font-bold text-gray-400">{stats.notCheckedIn}</p>
//                 </div>
//             </div>

//             {/* Table */}
//             <div className="overflow-x-auto">
//                 <div className="p-4">
//                     <div className="relative max-w-xs">
//                         <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
//                         <input
//                             type="text"
//                             placeholder="Search by name..."
//                             value={searchTerm}
//                             onChange={(e) => setSearchTerm(e.target.value)}
//                             className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
//                         />
//                     </div>
//                 </div>
//                 <table className="w-full text-sm">
//                     <thead className="bg-gray-50 border-b border-gray-200">
//                         <tr>
//                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
//                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
//                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Check In</th>
//                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Break</th>
//                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Check Out</th>
//                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
//                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Overtime</th>
//                         </tr>
//                     </thead>
//                     <tbody className="divide-y divide-gray-100">
//                         {filteredAttendance.length === 0 ? (
//                             <tr>
//                                 <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
//                                     <div className="flex flex-col items-center gap-2">
//                                         <span className="text-4xl">📋</span>
//                                         <p className="font-medium">No attendance records</p>
//                                         <p className="text-sm text-gray-400">No records found for this date</p>
//                                     </div>
//                                 </td>
//                             </tr>
//                         ) : (
//                             filteredAttendance.map((record) => {
//                                 const status = getStatusBadge(record.status);
//                                 const checkInTime = record.checkIn?.time?.seconds
//                                     ? new Date(record.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                     : '-';
//                                 const checkOutTime = record.checkOut?.time?.seconds
//                                     ? new Date(record.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                     : '-';
//                                 const breakMinutes = record.totalBreakTime || 0;
//                                 const workMinutes = record.workDuration || 0;
//                                 const hours = Math.floor(workMinutes / 60);
//                                 const minutes = workMinutes % 60;

//                                 return (
//                                     <tr key={record.id} className="hover:bg-gray-50 transition-colors">
//                                         <td className="px-4 py-3">
//                                             <div className="flex items-center gap-2">
//                                                 <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 flex items-center justify-center text-white font-semibold text-xs">
//                                                     {record.userName?.charAt(0) || 'U'}
//                                                 </div>
//                                                 <div>
//                                                     <p className="font-medium text-gray-800">{record.userName || 'Unknown'}</p>
//                                                     <p className="text-xs text-gray-400">{record.userId?.substring(0, 8)}...</p>
//                                                 </div>
//                                             </div>
//                                         </td>
//                                         <td className="px-4 py-3">
//                                             <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
//                                                 {status.label}
//                                             </span>
//                                         </td>
//                                         <td className="px-4 py-3 text-gray-600">{checkInTime}</td>
//                                         <td className="px-4 py-3 text-gray-600">{breakMinutes > 0 ? `${breakMinutes}m` : '-'}</td>
//                                         <td className="px-4 py-3 text-gray-600">{checkOutTime}</td>
//                                         <td className="px-4 py-3 text-gray-600">{workMinutes > 0 ? `${hours}h ${minutes}m` : '-'}</td>
//                                         <td className="px-4 py-3">
//                                             {record.overtime?.isOvertime ? (
//                                                 <span className={`text-xs font-medium ${record.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
//                                                     {record.overtime.minutes}m
//                                                     {!record.overtime.approved && ' (Pending)'}
//                                                 </span>
//                                             ) : (
//                                                 <span className="text-gray-400">-</span>
//                                             )}
//                                         </td>
//                                     </tr>
//                                 );
//                             })
//                         )}
//                     </tbody>
//                 </table>
//             </div>
//         </div>
//     );

//     // Render overtime requests view
//     const renderOvertimeView = () => (
//         <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
//             <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
//                 <div className="flex items-center gap-3">
//                     <div className="p-2 bg-orange-100 rounded-lg">
//                         <Award className="w-5 h-5 text-orange-600" />
//                     </div>
//                     <div>
//                         <h3 className="text-lg font-semibold text-gray-800">Overtime Requests</h3>
//                         <p className="text-sm text-gray-500">{overtimeRequests.length} pending approval</p>
//                     </div>
//                 </div>
//             </div>

//             {overtimeRequests.length === 0 ? (
//                 <div className="p-8 text-center text-gray-500">
//                     <div className="text-4xl mb-2">✅</div>
//                     <p className="font-medium">No pending overtime requests</p>
//                     <p className="text-sm text-gray-400">All overtime has been approved</p>
//                 </div>
//             ) : (
//                 <div className="overflow-x-auto">
//                     <table className="w-full text-sm">
//                         <thead className="bg-gray-50 border-b border-gray-200">
//                             <tr>
//                                 <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
//                                 <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
//                                 <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Check In</th>
//                                 <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Check Out</th>
//                                 <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Overtime</th>
//                                 <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
//                             </tr>
//                         </thead>
//                         <tbody className="divide-y divide-gray-100">
//                             {overtimeRequests.map((record) => {
//                                 const checkInTime = record.checkIn?.time?.seconds
//                                     ? new Date(record.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                     : '-';
//                                 const checkOutTime = record.checkOut?.time?.seconds
//                                     ? new Date(record.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                     : '-';
//                                 const date = record.date ? new Date(record.date).toLocaleDateString() : '-';

//                                 return (
//                                     <tr key={record.id} className="hover:bg-gray-50 transition-colors">
//                                         <td className="px-4 py-3">
//                                             <div className="flex items-center gap-2">
//                                                 <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-400 to-red-400 flex items-center justify-center text-white font-semibold text-xs">
//                                                     {record.userName?.charAt(0) || 'U'}
//                                                 </div>
//                                                 <div>
//                                                     <p className="font-medium text-gray-800">{record.userName || 'Unknown'}</p>
//                                                     <p className="text-xs text-gray-400">{record.userId?.substring(0, 8)}...</p>
//                                                 </div>
//                                             </div>
//                                         </td>
//                                         <td className="px-4 py-3 text-gray-600">{date}</td>
//                                         <td className="px-4 py-3 text-gray-600">{checkInTime}</td>
//                                         <td className="px-4 py-3 text-gray-600">{checkOutTime}</td>
//                                         <td className="px-4 py-3">
//                                             <span className="font-medium text-orange-600">{record.overtime?.minutes || 0}m</span>
//                                         </td>
//                                         <td className="px-4 py-3">
//                                             <button
//                                                 onClick={() => handleApproveOvertime(record.userId, record.date)}
//                                                 disabled={actionLoading}
//                                                 className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-xs flex items-center gap-1 disabled:opacity-50"
//                                             >
//                                                 <CheckCircle className="w-3 h-3" />
//                                                 Approve
//                                             </button>
//                                         </td>
//                                     </tr>
//                                 );
//                             })}
//                         </tbody>
//                     </table>
//                 </div>
//             )}
//         </div>
//     );

//     if (loading || isLoading) return <Loading />;

//     return (
//         <>
//             <Navbar user={user} />
//             <div className="max-w-7xl mx-auto px-4 py-6">
//                 {/* Header */}
//                 <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
//                     <div>
//                         <h1 className="text-2xl font-bold text-gray-800">Attendance Management</h1>
//                         <p className="text-sm text-gray-500">Monitor and manage employee attendance</p>
//                     </div>
//                     <div className="flex items-center gap-2">
//                         <button
//                             onClick={() => setViewMode('today')}
//                             className={`px-4 py-2 rounded-lg text-sm font-medium transition ${viewMode === 'today'
//                                 ? 'bg-pink-500 text-white'
//                                 : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
//                                 }`}
//                         >
//                             <Calendar className="w-4 h-4 inline mr-1" />
//                             Today
//                         </button>
//                         <button
//                             onClick={() => setViewMode('overtime')}
//                             className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1 ${viewMode === 'overtime'
//                                 ? 'bg-pink-500 text-white'
//                                 : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
//                                 }`}
//                         >
//                             <Award className="w-4 h-4" />
//                             Overtime
//                             {overtimeRequests.length > 0 && (
//                                 <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
//                                     {overtimeRequests.length}
//                                 </span>
//                             )}
//                         </button>
//                     </div>
//                 </div>

//                 {/* Content */}
//                 {viewMode === 'today' ? renderTodayView() : renderOvertimeView()}
//             </div>
//         </>
//     );
// };

// export default AdminAttendance;